import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useAtom } from 'jotai';
import type { AcctStateType } from 'stores/atoms/acctState';
import { AcctState } from 'stores/atoms/acctState';
import { submit } from 'utils/app';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

export type SettingProfileFormStateType = {
  userName: string;
  tel: string;
  roleDiv: number;
};

export type UseSettingProfileResult = {
  acctState: AcctStateType;
  processing: boolean;
  useFormMethods: UseFormReturn<SettingProfileFormStateType>;
  onSave: () => void;
};

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * プロフィール設定画面のロジックを集約するカスタムフック。
 */
export function useSettingProfile(): UseSettingProfileResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [acctState] = useAtom(AcctState);

  const [processing, setProcessing] = useState(false);
  const { updateAcctState, noticeMessageSuccess } = useAppState();
  const { postAcctUpdate } = useApiState();

  const useFormMethods = useForm<SettingProfileFormStateType>();
  const { setValue, handleSubmit } = useFormMethods;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- 初回マウント時にhoisting相当で_initStateを呼ぶ設計
    _initState(acctState);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ実行する設計
  }, []);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面のステートを初期化します。
   */
  const _initState = (state: AcctStateType) => {
    setValue('userName', state.user.name);
    setValue('tel', state.company.tel);
    setValue('roleDiv', state.company.member?.roleDiv);
  };

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 保存ボタン押下時の処理
   */
  const onSave = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit(async (data: SettingProfileFormStateType) => {
        try {
          await postAcctUpdate({
            companyId: acctState.company.id,
            name: data.userName,
          });

          const newState = await updateAcctState(['user']);
          _initState(newState as AcctStateType);

          noticeMessageSuccess('プロフィールの保存が完了しました。');
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  return {
    acctState,
    processing,
    useFormMethods,
    onSave,
  };
}
